/* Created: admin panel "Mollie-testbetaling (afgeschermd)" on the Transactiemails page.
   Starts one real Mollie TEST payment for a separate test order (EUR 1.00, test address only), shows the
   payment status and the mail jobs of that order (count, provider, status) and can resend the same payment
   notification to the webhook to verify no second mail is created. The global Mollie mode and the production
   mail provider are not touched. After paying, Mollie returns here with ?mollie_test=<order number>. */

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { supabase } from '@/lib/supabase';
import { CreditCard, RefreshCw, Repeat, Loader as Loader2, CircleCheck as CheckCircle2, CircleAlert as AlertCircle } from 'lucide-react';

interface TestJob {
  id: string; mode: string; provider: string; status: string; attempts: number;
  recipient_email: string | null; accepted_at: string | null; last_error: string | null; created_at: string;
}
interface TestStatus {
  order: { order_number: string; total: number; payment_status: string; paid_at: string | null; mollie_payment_id: string | null };
  jobs: TestJob[];
}

const ORDER_NUMBER = /^TEST-MOLLIE-\d{14}$/;
const formatDateTime = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'medium' }) : '-';

async function invoke(body: Record<string, unknown>) {
  const { data, error } = await supabase.functions.invoke('mollie-mail-test', { body });
  if (error) {
    let message = 'Onbekende fout';
    try {
      const ctx = (error as { context?: Response }).context;
      const parsed = ctx ? await ctx.json() : null;
      if (parsed?.error) message = parsed.error;
    } catch { /* keep generic message */ }
    throw new Error(message);
  }
  return data;
}

function Check({ ok, children }: { ok: boolean; children: ReactNode }) {
  return (
    <li className="flex items-start gap-2">
      {ok ? <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" /> : <AlertCircle className="w-4 h-4 mt-0.5 text-amber-600 shrink-0" />}
      <span className={ok ? 'text-[#1e2b22]' : 'text-[#5a6552]'}>{children}</span>
    </li>
  );
}

export default function MollieTestPanel({ onChanged }: { onChanged: () => void }) {
  const [params, setParams] = useSearchParams();
  const orderNumber = params.get('mollie_test');
  const validOrder = !!orderNumber && ORDER_NUMBER.test(orderNumber);
  const [status, setStatus] = useState<TestStatus | null>(null);
  const [busy, setBusy] = useState<'create' | 'status' | 'replay' | null>(null);
  const [confirmCreate, setConfirmCreate] = useState(false);
  const [lastReplay, setLastReplay] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!validOrder) return;
    setBusy('status');
    try {
      const data = await invoke({ action: 'status', order_number: orderNumber });
      if (!data?.order || !Array.isArray(data?.jobs)) throw new Error('Onvolledig antwoord');
      setStatus(data as TestStatus);
    } catch (e) {
      toast.error('Teststatus laden mislukt', { description: e instanceof Error ? e.message : undefined });
    } finally {
      setBusy(null);
    }
  }, [orderNumber, validOrder]);

  useEffect(() => { setStatus(null); setLastReplay(null); refresh(); }, [refresh]);

  async function handleCreate() {
    setConfirmCreate(false);
    setBusy('create');
    try {
      const data = await invoke({ action: 'create' });
      if (typeof data?.checkout_url !== 'string' || !data.checkout_url.startsWith('https://')) throw new Error('Geen Mollie-betaalpagina ontvangen');
      setParams({ mollie_test: data.order_number });
      window.location.assign(data.checkout_url);
    } catch (e) {
      toast.error('Testbetaling starten mislukt', { description: e instanceof Error ? e.message : undefined });
      setBusy(null);
    }
  }

  async function handleReplay() {
    setBusy('replay');
    try {
      const data = await invoke({ action: 'replay', order_number: orderNumber });
      const result = data?.webhook?.result ?? 'onbekend';
      setLastReplay(`${data?.http_status ?? '-'} / ${result}`);
      toast.success('Betaalmelding opnieuw verwerkt', { description: `Antwoord webhook: ${result}` });
    } catch (e) {
      toast.error('Betaalmelding herhalen mislukt', { description: e instanceof Error ? e.message : undefined });
    } finally {
      setBusy(null);
    }
    await refresh();
    onChanged();
  }

  const jobs = status?.jobs ?? [];
  const job = jobs[0];
  const paid = status?.order.payment_status === 'paid';

  return (
    <Card className="border border-[#e7e1d6] bg-white">
      <CardHeader className="bg-[#fcfaf6] border-b border-[#e7e1d6]">
        <CardTitle className="flex items-center gap-2 text-[#1e2b22]"><CreditCard className="w-5 h-5" />Mollie-testbetaling (afgeschermd)</CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-6">
        <p className="text-sm text-[#5a6552] max-w-3xl">
          Maakt een aparte testbestelling van EUR 1,00 en een echte betaling in de Mollie-testomgeving, met de gewone betaalmelding.
          De bevestiging gaat altijd via Brevo en alleen naar julan.tw@gmail.com, zonder kopie naar info@buitnplanten.nl.
          De Mollie-instelling van de winkel en de productie-instelling voor mails blijven ongewijzigd.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button className="bg-[#1e2b22] hover:bg-[#2a3a2f] text-white" disabled={!!busy} onClick={() => setConfirmCreate(true)}>
            {busy === 'create' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}Nieuwe testbetaling starten
          </Button>
          {validOrder && (
            <>
              <Button variant="outline" className="border-[#e7e1d6]" disabled={!!busy} onClick={refresh}>
                <RefreshCw className={`w-4 h-4 mr-2 ${busy === 'status' ? 'animate-spin' : ''}`} />Status vernieuwen
              </Button>
              <Button variant="outline" className="border-[#e7e1d6]" disabled={!!busy || !paid} onClick={handleReplay}>
                {busy === 'replay' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Repeat className="w-4 h-4 mr-2" />}Betaalmelding herhalen
              </Button>
            </>
          )}
        </div>

        {orderNumber && !validOrder && <p className="text-sm text-red-700">Onbekend testbestelnummer in de adresbalk.</p>}

        {status && (
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2 text-sm">
              <p className="font-semibold text-[#1e2b22]">Testbestelling {status.order.order_number}</p>
              <p className="text-[#5a6552]">Mollie-betaling: {status.order.mollie_payment_id ?? '-'}</p>
              <p className="text-[#5a6552]">Betaalstatus: <Badge variant="outline">{status.order.payment_status}</Badge></p>
              <p className="text-[#5a6552]">Betaald op: {formatDateTime(status.order.paid_at)}</p>
              {lastReplay && <p className="text-[#5a6552]">Laatste herhaling (antwoord webhook): {lastReplay}</p>}
            </div>
            <ul className="space-y-2 text-sm">
              <Check ok={paid}>Bestelling staat op betaald</Check>
              <Check ok={jobs.length === 1}>Precies één mailopdracht (nu: {jobs.length})</Check>
              <Check ok={job?.provider === 'brevo' && job?.mode === 'test'}>
                Provider Brevo, testmodus{job ? ` (nu: ${job.provider}, ${job.mode})` : ''}
              </Check>
              <Check ok={job?.recipient_email === 'julan.tw@gmail.com'}>Alleen naar julan.tw@gmail.com{job?.recipient_email ? ` (nu: ${job.recipient_email})` : ''}</Check>
              <Check ok={job?.status === 'accepted' || job?.status === 'delivered'}>
                Geaccepteerd door Brevo{job ? ` (nu: ${job.status}${job.accepted_at ? `, ${formatDateTime(job.accepted_at)}` : ''})` : ''}
              </Check>
              {job?.last_error && <li className="text-red-700 break-words">{job.last_error}</li>}
            </ul>
          </div>
        )}
      </CardContent>

      <AlertDialog open={confirmCreate} onOpenChange={setConfirmCreate}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mollie-testbetaling starten?</AlertDialogTitle>
            <AlertDialogDescription>
              Er wordt een testbestelling (TEST-MOLLIE-...) van EUR 1,00 aangemaakt en je gaat naar de Mollie-testbetaalpagina.
              Er wordt geen echt geld afgeschreven. Kies daar de status "Paid"; daarna kom je hier terug.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={handleCreate}>Starten</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
